import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { todayISO } from "@/lib/calendar";
import { friendlyError } from "@/lib/errors";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import useMediaQuery from "@/hooks/useMediaQuery";
import { ListSkeleton, ErrorState } from "@/components/shared/StateViews";
import { BookMarked, PenLine, Check, Loader2, CalendarPlus } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = {
  script: "Roteiro",
  carrossel: "Carrossel",
  calendar: "Calendário",
  metrics: "Métricas",
  pricing: "Precificação",
};

// Bottom sheet that adds one or more entries to the content calendar (Epic 11.2).
// Two origins: pick saved artifacts from the Library (multi-select → N POSTs), or
// type a free Manual title. `preselectedArtifact` opens straight on Library with
// that artifact ticked — the 1-click path from AgentChat / Library cards.
export default function AddToCalendarSheet({
  open,
  onOpenChange,
  defaultDate,
  preselectedArtifact = null,
  onAdded,
}) {
  const [tab, setTab] = useState("library");
  const [date, setDate] = useState(defaultDate || todayISO());
  const [time, setTime] = useState("");
  const [title, setTitle] = useState("");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // >=md (Tailwind md breakpoint): centered modal dialog; <md: bottom sheet.
  const isDesktop = useMediaQuery("(min-width: 768px)");

  // Reset the form each time the sheet opens.
  useEffect(() => {
    if (!open) return;
    setDate(defaultDate || todayISO());
    setTime("");
    setTitle("");
    setError(null);
    if (preselectedArtifact) {
      setTab("library");
      setSelected(new Set([preselectedArtifact.id]));
    } else {
      setSelected(new Set());
    }
  }, [open, defaultDate, preselectedArtifact]);

  // Load saved artifacts for the Library origin.
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get("/artifacts", { params: { status: "saved" } });
        if (!alive) return;
        let list = data || [];
        // Keep a preselected artifact visible even if the saved list is stale.
        if (preselectedArtifact && !list.some((i) => i.id === preselectedArtifact.id)) {
          list = [preselectedArtifact, ...list];
        }
        setItems(list);
      } catch (err) {
        if (alive) setError(friendlyError(err));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, preselectedArtifact]);

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const canConfirm =
    Boolean(date) && (tab === "library" ? selected.size > 0 : title.trim().length > 0);

  const confirm = async () => {
    if (!canConfirm || saving) return;
    setSaving(true);
    try {
      const base = { entry_date: date };
      if (time) base.entry_time = time;
      if (tab === "library") {
        const ids = [...selected];
        await Promise.all(ids.map((artifact_id) => api.post("/calendar", { ...base, artifact_id })));
        toast.success(ids.length === 1 ? "Adicionado ao calendário ✦" : `${ids.length} itens adicionados ✦`);
      } else {
        await api.post("/calendar", { ...base, title: title.trim() });
        toast.success("Adicionado ao calendário ✦");
      }
      onOpenChange(false);
      onAdded?.();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setSaving(false);
    }
  };

  const formBody = (
    <>
      {/* Origin selector */}
        <div className="mt-5 grid grid-cols-2 gap-2" role="tablist" aria-label="Origem">
          <OriginPill
            testid="add-cal-tab-library"
            active={tab === "library"}
            icon={BookMarked}
            label="Biblioteca"
            onClick={() => setTab("library")}
          />
          <OriginPill
            testid="add-cal-tab-manual"
            active={tab === "manual"}
            icon={PenLine}
            label="Manual"
            onClick={() => setTab("manual")}
          />
        </div>

        <div className="mt-5">
          {tab === "library" ? (
            loading ? (
              <ListSkeleton count={3} />
            ) : error ? (
              <ErrorState message={error} testid="add-cal-error" />
            ) : items.length === 0 ? (
              <div
                data-testid="add-cal-library-empty"
                className="bg-brand-elevated border border-dashed border-brand-line rounded-2xl p-8 text-center"
              >
                <BookMarked className="w-7 h-7 text-brand-rose mx-auto mb-3" />
                <p className="text-[14px] text-brand-ink2">
                  Você ainda não tem conteúdos salvos. Use a aba Manual para criar um lembrete.
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[38vh] overflow-y-auto overflow-x-hidden scrollbar-soft pr-0.5" data-testid="add-cal-library-list">
                {items.map((it) => {
                  const on = selected.has(it.id);
                  return (
                    <button
                      key={it.id}
                      type="button"
                      data-testid={`add-cal-item-${it.id}`}
                      aria-pressed={on}
                      onClick={() => toggle(it.id)}
                      className={`w-full text-left flex items-center gap-3 rounded-2xl p-3.5 border transition-colors ${
                        on ? "border-brand-rose bg-brand-rose/5" : "border-brand-line hover:border-brand-rose/60"
                      }`}
                    >
                      <span
                        className={`shrink-0 w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${
                          on ? "bg-brand-rose border-brand-rose text-white" : "border-brand-line text-transparent"
                        }`}
                      >
                        <Check className="w-3.5 h-3.5" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[10px] font-ui tracking-[0.2em] uppercase text-brand-rose">
                          {TYPE_LABEL[it.type] || it.type}
                        </span>
                        <span className="block font-display text-[15px] text-brand-ink leading-tight truncate">
                          {it.title}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )
          ) : (
            <div>
              <label className="block text-[11px] tracking-[0.2em] uppercase text-brand-ink2 font-ui mb-2">
                Título do conteúdo
              </label>
              <input
                data-testid="add-cal-manual-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex.: Reels sobre pós-depilação"
                className="w-full bg-brand-elevated border border-brand-line rounded-2xl px-4 py-3 text-[16px] text-brand-ink placeholder:text-brand-ink3 focus:outline-none focus:border-brand-rose transition-colors min-h-[48px]"
              />
            </div>
          )}
        </div>

        {/* Day + optional time */}
        <div className="mt-5 grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] tracking-[0.2em] uppercase text-brand-ink2 font-ui mb-2">
              Dia
            </label>
            <input
              data-testid="add-cal-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full bg-brand-elevated border border-brand-line rounded-2xl px-4 py-3 text-[16px] text-brand-ink focus:outline-none focus:border-brand-rose transition-colors min-h-[48px]"
            />
          </div>
          <div>
            <label className="block text-[11px] tracking-[0.2em] uppercase text-brand-ink2 font-ui mb-2">
              Hora <span className="text-brand-ink3 normal-case tracking-normal">(opcional)</span>
            </label>
            <input
              data-testid="add-cal-time"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="w-full bg-brand-elevated border border-brand-line rounded-2xl px-4 py-3 text-[16px] text-brand-ink focus:outline-none focus:border-brand-rose transition-colors min-h-[48px]"
            />
          </div>
        </div>

        <button
          type="button"
          data-testid="add-cal-confirm"
          onClick={confirm}
          disabled={!canConfirm || saving}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-brand-rose hover:bg-brand-roseHover text-white px-6 py-3.5 rounded-full text-[14px] font-ui transition-colors min-h-[48px] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarPlus className="w-4 h-4" />}
          {tab === "library" && selected.size > 1 ? `Adicionar ${selected.size} itens` : "Adicionar ao calendário"}
        </button>
    </>
  );

  const titleNode = (
    <>
      <CalendarPlus className="w-5 h-5 text-brand-rose" />
      Adicionar ao calendário
    </>
  );
  const descriptionText = "Escolha um conteúdo salvo ou crie um lembrete manual.";

  // >=md: centered modal dialog (max-w 520px). <md: bottom sheet.
  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          data-testid="add-to-calendar-sheet"
          className="bg-brand-surface border-brand-line max-w-[560px] max-h-[88vh] overflow-y-auto overflow-x-hidden scrollbar-soft"
        >
          <DialogHeader className="text-left">
            <DialogTitle className="font-display text-2xl text-brand-ink flex items-center gap-2">
              {titleNode}
            </DialogTitle>
            <DialogDescription className="text-brand-ink2">
              {descriptionText}
            </DialogDescription>
          </DialogHeader>
          {formBody}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        data-testid="add-to-calendar-sheet"
        className="bg-brand-surface border-brand-line rounded-t-3xl max-h-[88vh] overflow-y-auto overflow-x-hidden scrollbar-soft"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-2xl text-brand-ink flex items-center gap-2">
            {titleNode}
          </SheetTitle>
          <SheetDescription className="text-brand-ink2">
            {descriptionText}
          </SheetDescription>
        </SheetHeader>
        {formBody}
      </SheetContent>
    </Sheet>
  );
}

function OriginPill({ active, icon: Icon, label, onClick, testid }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-testid={testid}
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl text-[13.5px] font-ui transition-colors min-h-[48px] border ${
        active
          ? "bg-brand-rose text-white border-brand-rose"
          : "bg-brand-elevated text-brand-ink2 border-brand-line hover:border-brand-rose/60"
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}
